import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, ApiError, type TournamentLifecycle } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { formatDate, formatDateTime } from "../lib/dateFormat";
import {
  championsAnnouncement,
  closeConfirmMessage,
  closeLaterLine,
  closePendingLines,
  lifecycleEventText,
  podiumByDivision,
} from "../lib/tournamentLifecycle";

/** Resumen del torneo → «Cerrar torneo» (Eduardo 2026-09-27): qué falta,
 * cerrar, posiciones finales, comunicado de campeones y reabrir con motivo. */
export default function TournamentLifecycleCard({
  token,
  tournamentId,
  tournamentName,
  onChange,
}: {
  token: string;
  tournamentId: number;
  tournamentName: string;
  onChange?: () => void;
}) {
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const navigate = useNavigate();
  const [lifecycle, setLifecycle] = useState<TournamentLifecycle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState("");
  const [notifyPlayers, setNotifyPlayers] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.tournaments.lifecycle(token, tournamentId);
      setLifecycle(response.data.lifecycle);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar el cierre del torneo.");
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleClose() {
    if (!lifecycle) return;
    const confirmed = await askConfirm(closeConfirmMessage(tournamentName, lifecycle.preview), {
      acceptLabel: "Sí, cerrar torneo",
      tone: "danger",
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.tournaments.close(token, tournamentId);
      setLifecycle(response.data.lifecycle);
      onChange?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cerrar el torneo.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReopen() {
    const cleaned = reason.trim();
    if (cleaned.length < 3) {
      setError("Escribe el motivo de la reapertura.");
      return;
    }
    const confirmed = await askConfirm(
      `¿Reabrir «${tournamentName}» para editarlo?\n\nMotivo: ${cleaned}\n\n` +
        (notifyPlayers
          ? "Los jugadores recibirán los avisos automáticos de los cambios."
          : "Los cambios NO se avisarán a los jugadores.") +
        " Cuando termines, vuelve a cerrarlo.",
      { acceptLabel: "Sí, reabrir" },
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.tournaments.reopen(token, tournamentId, {
        reason: cleaned,
        notify_players: notifyPlayers,
      });
      setLifecycle(response.data.lifecycle);
      setReopening(false);
      setReason("");
      setNotifyPlayers(false);
      onChange?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo reabrir el torneo.");
    } finally {
      setBusy(false);
    }
  }

  async function handleChampionsDraft() {
    if (!lifecycle) return;
    const draft = championsAnnouncement(tournamentName, lifecycle.final_results);
    setBusy(true);
    setError(null);
    try {
      // Queda como BORRADOR: el admin lo revisa en Comunicados y decide si lo manda.
      await api.communications.create(token, tournamentId, { title: draft.title, body: draft.body });
      navigate(`/admin/tournaments/${tournamentId}/communications`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo crear el borrador del comunicado.");
    } finally {
      setBusy(false);
    }
  }

  if (!lifecycle) {
    return error ? (
      <section className="card" data-no-toast>
        <p className="form-error">{error}</p>
      </section>
    ) : null;
  }

  const podium = podiumByDivision(lifecycle.final_results);
  const events = lifecycle.events.slice(0, 5);

  if (lifecycle.closed) {
    return (
      <section className="card tournament-lifecycle-card tournament-lifecycle-card--closed" data-no-toast>
        <p className="eyebrow">Torneo cerrado</p>
        <h2 className="card__title">
          Cerrado{lifecycle.closed_at ? ` el ${formatDateTime(lifecycle.closed_at)}` : ""}
          {lifecycle.closed_by_name ? ` por ${lifecycle.closed_by_name}` : ""}
        </h2>
        <p className="muted">
          Solo lectura: resultados, grupos, draw, calendario, inscripciones y ranking ya no cambian. Pagos, reembolsos y
          comunicados siguen disponibles.
        </p>
        {podium.length > 0 ? (
          <div className="responsive-table-wrap">
            <table className="data-table tournament-lifecycle-card__podium">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Campeón</th>
                  <th>Subcampeón</th>
                </tr>
              </thead>
              <tbody>
                {podium.map((row) => (
                  <tr key={row.division_name}>
                    <td>{row.division_name}</td>
                    {row.champion || row.runner_up ? (
                      <>
                        <td>{row.champion ?? "—"}</td>
                        <td>{row.runner_up ?? "—"}</td>
                      </>
                    ) : (
                      <td colSpan={2}>Primeros de grupo: {row.group_leaders.join(", ")}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">Sin posiciones finales registradas (no hubo final ni grupo terminado).</p>
        )}
        {lifecycle.final_results.length > 0 ? (
          <details className="tournament-lifecycle-card__results">
            <summary>Posiciones finales de todos ({lifecycle.final_results.length})</summary>
            <ul>
              {lifecycle.final_results.map((row) => (
                <li key={row.participant_id}>
                  <strong>{row.name}</strong> · {row.division_name} · {row.label}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        <div className="form__actions">
          {podium.length > 0 ? (
            <button type="button" className="btn" onClick={() => void handleChampionsDraft()} disabled={busy}>
              Redactar comunicado de campeones
            </button>
          ) : null}
          {!reopening ? (
            <button type="button" className="btn btn--ghost" onClick={() => setReopening(true)} disabled={busy}>
              Reabrir para editar
            </button>
          ) : null}
        </div>
        {reopening ? (
          <div className="form tournament-lifecycle-card__reopen">
            <label>
              Motivo de la reapertura
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Ej. corregir el marcador de la final de Primera"
              />
            </label>
            <label className="form__checkbox">
              <input type="checkbox" checked={notifyPlayers} onChange={(event) => setNotifyPlayers(event.target.checked)} />
              <span>Avisar a los jugadores de los cambios</span>
            </label>
            <div className="form__actions">
              <button type="button" className="btn" onClick={() => void handleReopen()} disabled={busy}>
                {busy ? "Reabriendo…" : "Reabrir"}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setReopening(false)} disabled={busy}>
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
        {events.length > 0 ? <LifecycleEvents events={events} /> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
      </section>
    );
  }

  const pendingLines = lifecycle.preview ? closePendingLines(lifecycle.preview) : [];
  const laterLine = lifecycle.preview ? closeLaterLine(lifecycle.preview) : null;
  const dueMatches = lifecycle.preview
    ? (lifecycle.preview.due_matches_total ?? lifecycle.preview.pending_matches_total)
    : 0;
  const previewPodium = podiumByDivision(lifecycle.preview?.champions ?? []);
  return (
    <section className="card tournament-lifecycle-card" data-no-toast>
      {lifecycle.reopened_at ? (
        <p className="tournament-lifecycle-card__reopened">
          Reabierto el {formatDateTime(lifecycle.reopened_at)}
          {lifecycle.reopened_by_name ? ` por ${lifecycle.reopened_by_name}` : ""}: «{lifecycle.reopen_reason}».{" "}
          {lifecycle.reopen_notify_players
            ? "Los jugadores reciben avisos de los cambios."
            : "Los cambios no se avisan a los jugadores."}{" "}
          Cuando termines, ciérralo de nuevo.
        </p>
      ) : null}
      <p className="eyebrow">
        {lifecycle.suggest_close && lifecycle.end_date ? `Terminó el ${formatDate(lifecycle.end_date)}` : "Cierre del torneo"}
      </p>
      <h2 className="card__title">{lifecycle.suggest_close ? "¿Lo cierras?" : "Cerrar torneo"}</h2>
      <p className="muted">
        Cuando el torneo termine, ciérralo: queda en solo lectura y los jugadores lo ven como terminado con su resultado
        final. Puedes reabrirlo después con un motivo.
      </p>
      <ul className="aviso-checklist">
        <li>Los partidos sin resultado quedan como «No jugado».</li>
        <li>Se guarda la posición final de cada jugador (campeón, finalista, lugar en su grupo).</li>
        <li>El ranking hace su último cálculo y queda congelado.</li>
        <li>El link de inscripción se cierra; pagos, reembolsos y comunicados siguen disponibles.</li>
      </ul>
      {pendingLines.length > 0 ? (
        <div className="tournament-lifecycle-card__pending">
          <strong>Pendiente</strong>
          <ul>
            {pendingLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {dueMatches > 0 ? (
            <Link to={`/admin/tournaments/${tournamentId}/results`}>Capturar resultados pendientes</Link>
          ) : null}
        </div>
      ) : laterLine ? null : (
        <p className="muted">Nada pendiente: todos los partidos tienen resultado.</p>
      )}
      {laterLine ? <p className="muted">{laterLine}</p> : null}
      {previewPodium.some((row) => row.champion) ? (
        <p className="muted">
          Campeones al cerrar:{" "}
          {previewPodium
            .filter((row) => row.champion)
            .map((row) => `${row.division_name}: ${row.champion}`)
            .join(" · ")}
        </p>
      ) : null}
      <div className="form__actions">
        <button type="button" className="btn btn--danger" onClick={() => void handleClose()} disabled={busy}>
          {busy ? "Cerrando…" : "Cerrar torneo"}
        </button>
      </div>
      {events.length > 0 ? <LifecycleEvents events={events} /> : null}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
    </section>
  );
}

function LifecycleEvents({ events }: { events: TournamentLifecycle["events"] }) {
  return (
    <details className="tournament-lifecycle-card__events">
      <summary>Historial del cierre</summary>
      <ul>
        {events.map((event, index) => (
          <li key={`${event.action}-${event.at ?? index}`}>
            {event.at ? `${formatDateTime(event.at)} · ` : ""}
            {lifecycleEventText(event)}
          </li>
        ))}
      </ul>
    </details>
  );
}
